// R5.2 / R5.7 — LAYER 4: cross-cutting descriptors (spec §4.4), rendered PER ENTRY inside the entry card.
//
// "Chips/rows from `EntryDescriptors`; collaboration under `CS-DESC-COLLABORATION`, never banded, never a gap.
// Scope rows always with `CS-SCOPE-DESCRIPTIVE`."
//
// DESCRIPTIVE, NOT BANDED — which is the whole point of the layer: OQ-6 demoted collaboration FROM a domain TO a
// descriptor precisely because banding it would measure writing convention rather than experience. So nothing
// here renders a `BandChip`, nothing here renders "not shown" for an absent descriptor (an entry with no topic
// tags simply has no topic-tag row), and `CS-SCOPE-DESCRIPTIVE` accompanies every scope row to say in words that
// wider is not better.
//
// PROVENANCE, EXACTLY AS §1.2b PINS IT — this is the layer where the two classes sit side by side:
//   * `topic_tags[].value` is `applicant_verbatim` (one of exactly TWO pinned patterns on the whole payload),
//     path-locally evidence-bound to the very `topic_tag` fact whose value IS the tag. It renders through
//     `ApplicantText`: verbatim, unscanned by the banned-output sweep, never templated from.
//   * `venue_or_award_scopes[].value`, `recognition_types[].value`, `dissemination_event_types[].value` and
//     `recurrence.value.frequency` are `opaque_metadata` (reclassified rev 11, R5-10P-02 — extractor-NORMALIZED
//     enum strings, not verbatim prose, and moved INTO the scanner's scope). They are not applicant text and are
//     not registered copy, so this surface does not print them as bare labels; each value's own EVIDENCE is what
//     renders — the applicant's words behind it, through the standard lookup.
//
// R5.7 (owner direction 2026-08-22): no standalone "Patterns across entries" section any more — the rows render
// inside the entry they describe, and an entry with no descriptors renders no descriptor block at all.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import { ApplicantText, Copy, EvidenceReveal } from './renderContent';
import { resolveEvidence, type EvidenceIndex } from './evidenceIndex';
import { BODY_STYLE, MUTED_STYLE, SUB_LABEL_STYLE } from './styles';

type Entry = RubricReadPublicV1['entries'][number];
type DescriptorSet = Entry['descriptors'];

export function hasDescriptors(d: DescriptorSet): boolean {
  return (
    d.topic_tags.length > 0 ||
    d.venue_or_award_scopes.length > 0 ||
    d.recognition_types.length > 0 ||
    d.dissemination_event_types.length > 0 ||
    d.recurrence !== null ||
    d.trajectory.length > 0 ||
    d.collaboration !== null
  );
}

/** One entry's descriptor rows, or nothing when the entry has none. */
export function EntryDescriptors({ entry, evidence }: { entry: Entry; evidence: EvidenceIndex }) {
  const d = entry.descriptors;
  if (!hasDescriptors(d)) return null;
  const scopeEvidence = [
    ...d.venue_or_award_scopes.flatMap((s) => resolveEvidence(evidence, s.evidence_fact_ids)),
    ...d.recognition_types.flatMap((s) => resolveEvidence(evidence, s.evidence_fact_ids)),
    ...d.dissemination_event_types.flatMap((s) => resolveEvidence(evidence, s.evidence_fact_ids)),
  ];
  return (
    <div data-testid="rubric-entry-descriptors">
      {/* The block's label is the registered layer name — a `<p>`, not a heading: the entry's own `<h3>` heads the
          card, and a sub-block inside it must not extend the outline. */}
      <p style={SUB_LABEL_STYLE}>
        <Copy id="CS-SECTION-DESCRIPTORS" />
      </p>

      {/* Topic tags — the applicant's OWN words, verbatim, each beside the fact that evidences it. */}
      {d.topic_tags.length > 0 && (
        <ul style={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', margin: '0 0 var(--space-2)', padding: 0 }} data-testid="rubric-topic-tags">
          {d.topic_tags.map((t, i) => (
            <li key={`${t.value}-${i}`} style={{ ...MUTED_STYLE, margin: 0 }}>
              <ApplicantText text={t.value} />
            </li>
          ))}
        </ul>
      )}

      {/* Scope rows ALWAYS carry §5.1's descriptive framing — "Wider is not better". */}
      {scopeEvidence.length > 0 && (
        <div data-testid="rubric-scope-row">
          <p style={BODY_STYLE}>
            <Copy id="CS-SCOPE-DESCRIPTIVE" />
          </p>
          <EvidenceReveal evidence={scopeEvidence} />
        </div>
      )}

      {/* Collaboration: NEVER banded, NEVER a gap — present with its evidence, or absent entirely. */}
      {d.collaboration && (
        <div data-testid="rubric-collaboration">
          <p style={BODY_STYLE}>
            <Copy id={d.collaboration.copy_string_id} />
          </p>
          <EvidenceReveal evidence={resolveEvidence(evidence, [d.collaboration.evidence_fact_id])} />
        </div>
      )}

      {/* Recurrence and trajectory are structured values (dates, years, a normalized frequency), not prose.
          Their evidence is what a reader can check, so their evidence is what renders. */}
      {d.recurrence && <EvidenceReveal evidence={resolveEvidence(evidence, d.recurrence.evidence_fact_ids)} />}
      {d.trajectory.map((t, i) => (
        <EvidenceReveal key={i} evidence={resolveEvidence(evidence, t.evidence_fact_ids)} />
      ))}
    </div>
  );
}
